/**
 * SupervisorPanel - Displays supervisor events and policy enforcement status
 * 
 * Shows:
 * - Current guardian iteration count
 * - Completed sub-tasks
 * - Policy violations and decisions
 * - Auto-actions taken
 * - Retry attempts
 */
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  Shield, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type {
  PolicyDecisionPayload,
  RetryAttemptPayload,
  GuardianIterationPayload,
  SubTaskCompletedPayload,
} from '@/lib/signalRService'

export interface SupervisorPanelProps {
  /** Policy decisions made by the supervisor */
  policyDecisions?: PolicyDecisionPayload[]
  /** Retry attempts */
  retryAttempts?: RetryAttemptPayload[]
  /** Guardian iteration updates */
  guardianIterations?: GuardianIterationPayload[]
  /** Completed sub-tasks */
  subTasks?: SubTaskCompletedPayload[]
  /** Current iteration number */
  currentIteration?: number
  /** Maximum iterations */
  maxIterations?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Get badge variant for policy decision
 */
function getDecisionVariant(decision: string): 'default' | 'destructive' | 'secondary' {
  switch (decision.toLowerCase()) {
    case 'blocked':
    case 'rejected':
      return 'destructive'
    case 'allowed':
    case 'approved':
      return 'default'
    default:
      return 'secondary'
  }
}

export function SupervisorPanel({
  policyDecisions = [],
  retryAttempts = [],
  guardianIterations = [],
  subTasks = [],
  currentIteration,
  maxIterations,
  className,
}: SupervisorPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Calculate summary stats
  const completedTasks = subTasks.filter(t => t.success).length
  const totalRetries = retryAttempts.length
  const blockedPolicies = policyDecisions.filter(p => 
    p.decision.toLowerCase() === 'blocked' || p.decision.toLowerCase() === 'rejected'
  ).length

  // Show panel only if there are supervisor events
  const hasEvents = policyDecisions.length > 0 || 
                    retryAttempts.length > 0 || 
                    guardianIterations.length > 0 || 
                    subTasks.length > 0

  if (!hasEvents) {
    return null
  }

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Supervisor Status</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {currentIteration !== undefined && maxIterations !== undefined && (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Iteration:</span>
                <Badge variant="outline" className="ml-auto">
                  {currentIteration}/{maxIterations}
                </Badge>
              </div>
            )}
            {subTasks.length > 0 && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                <span className="text-muted-foreground">Tasks:</span>
                <Badge variant="outline" className="ml-auto">
                  {completedTasks}/{subTasks.length}
                </Badge>
              </div>
            )}
            {totalRetries > 0 && (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-3 w-3 text-yellow-500" />
                <span className="text-muted-foreground">Retries:</span>
                <Badge variant="outline" className="ml-auto">{totalRetries}</Badge>
              </div>
            )}
            {policyDecisions.length > 0 && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-3 w-3 text-orange-500" />
                <span className="text-muted-foreground">Policies:</span>
                <Badge variant="outline" className="ml-auto">{blockedPolicies} blocked</Badge>
              </div>
            )}
          </div>

          {/* Recent Events */}
          <ScrollArea className="h-48">
            <div className="space-y-2 pr-3">
              {/* Guardian Iterations */}
              {guardianIterations.map((iter, index) => (
                <div
                  key={`iter-${index}`}
                  className="flex items-start gap-2 p-2 rounded bg-muted/50 text-xs"
                >
                  <RefreshCw className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">
                      Guardian Iteration {iter.iteration}/{iter.maxIterations}
                    </div>
                    <div className="text-muted-foreground text-[10px]">
                      {new Date(iter.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}

              {/* Sub-Tasks */}
              {subTasks.map((task, index) => (
                <div
                  key={`task-${index}`}
                  className="flex items-start gap-2 p-2 rounded bg-muted/50 text-xs"
                >
                  {task.success ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{task.taskName}</div>
                    <div className="text-muted-foreground text-[10px]">
                      {new Date(task.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}

              {/* Policy Decisions */}
              {policyDecisions.map((decision, index) => (
                <div
                  key={`policy-${index}`}
                  className="flex items-start gap-2 p-2 rounded bg-muted/50 text-xs"
                >
                  <Shield className="h-3 w-3 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{decision.policy}</span>
                      <Badge variant={getDecisionVariant(decision.decision)} className="text-[10px] px-1.5 py-0">
                        {decision.decision}
                      </Badge>
                    </div>
                    {decision.reason && (
                      <div className="text-muted-foreground text-[10px]">{decision.reason}</div>
                    )}
                    <div className="text-muted-foreground text-[10px]">
                      {new Date(decision.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}

              {/* Retry Attempts */}
              {retryAttempts.map((retry, index) => (
                <div
                  key={`retry-${index}`}
                  className="flex items-start gap-2 p-2 rounded bg-muted/50 text-xs"
                >
                  <RefreshCw className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">Retry {retry.attempt}/{retry.maxAttempts}</span>
                    </div>
                    <div className="text-muted-foreground text-[10px]">{retry.reason}</div>
                    <div className="text-muted-foreground text-[10px]">
                      {new Date(retry.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  )
}
